import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Stethoscope, Lock, Mail } from "lucide-react";

export default function VetLogin({ onLogin }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "vet") { setError("This account does not have vet access."); return; }
      onLogin?.();
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0e1812] flex items-center justify-center px-6" data-testid="vet-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <img src="/logo-dark-256.png" alt="PawLinx" className="h-20 w-auto mx-auto mb-5" data-testid="pawlinx-logo" />
          <p className="font-manrope text-xs text-emerald-300/80 tracking-[0.25em] uppercase flex items-center justify-center gap-2"><Stethoscope className="w-3.5 h-3.5" strokeWidth={1.5} /> Vet Portal</p>
          <p className="font-manrope text-sm text-white/40 mt-2">Sign in to review prescriptions</p>
        </div>
        <form onSubmit={submit} className="bg-[#142318] border border-white/10 rounded-2xl p-8 space-y-5">
          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5 font-manrope">{error}</p>}
          <div className="space-y-1.5">
            <label className="font-manrope text-sm font-medium text-white/60">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-emerald-400" data-testid="vet-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="font-manrope text-sm font-medium text-white/60">Password</label>
              <Link to="/forgot-password" className="font-manrope text-xs font-medium text-emerald-400 hover:underline">Forgot password?</Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-emerald-400" data-testid="vet-password" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0e1812] rounded-xl font-manrope font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-testid="vet-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
